import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Dashboard,
  DashboardPresentation,
  DashboardSummary,
  DashboardsState,
  Widget,
  WidgetPositioning,
} from "@src/types/dashboards";

const DEFAULT_COLUMN_COUNT = 24;

// Normalize both list summaries (no widgets/positioning/audit fields) and full
// dashboards into a complete Dashboard. Defaults are laid down first, then the
// payload overrides whatever it actually carries.
// Re-key positioning so every entry is keyed by its widget's id (widgets are
// dashed uuid, but the backend returns positioning keyed by the *undashed* uuid
// — leaving both produced duplicate keys in the change handler's PATCH). Orphan
// positioning entries (deleted widgets) are dropped.
export const normalizeDashboard = (
  d: Dashboard | DashboardSummary,
): Dashboard => {
  const base = {
    created_by: "",
    last_updated_by: "",
    widgets: {},
    positioning: {},
    ...d,
  } as Dashboard;
  const widgets = base.widgets || {};
  const positioning = base.positioning || {};
  const normalizedPositioning: Record<string, WidgetPositioning> = {};
  Object.keys(widgets).forEach((widgetId) => {
    const pos =
      positioning[widgetId] ?? positioning[widgetId.replace(/-/g, "")];
    if (pos) normalizedPositioning[widgetId] = pos;
  });
  return {
    ...base,
    widgets,
    positioning: normalizedPositioning,
    columnCount: base.columnCount || DEFAULT_COLUMN_COUNT,
  };
};

const initialState: DashboardsState = {
  dashboards: [],
  selectedDashboardId: null,
  defaultDashboardId: null,
  pendingWidgetIds: [],
};

const dashboardsSlice = createSlice({
  name: "dashboards",
  initialState,
  reducers: {
    setDashboards(
      state,
      action: PayloadAction<(Dashboard | DashboardSummary)[]>,
    ) {
      state.dashboards = action.payload.map((incomingRaw) => {
        // Shape-based discriminator, NOT emptiness-based: a
        // `Dashboard` with legitimately empty widgets is
        // indistinguishable from a `DashboardSummary` once the
        // reducer has defaulted widgets to `{}`, so the previous
        // "if incoming.widgets has keys → authoritative, else →
        // merge from existing" wording resurrected stale widgets
        // for any dashboard whose last widget was deleted
        // (advisor score 85). We check the raw payload shape:
        // anything that has a `widgets` field at all is treated
        // as a full Dashboard and trusted.
        const isFullDashboard =
          (incomingRaw as Partial<Dashboard>).widgets !== undefined;
        if (isFullDashboard) {
          return normalizeDashboard(incomingRaw as Dashboard);
        }
        // `DashboardSummary` shape: the list endpoint hands
        // back entries that carry no widgets, no positioning
        // and no `presentation`. When the slice ALREADY holds
        // the full object for this id (the detail GET wrote it,
        // e.g. the user opened this dashboard in the editor and
        // Redux carries the entry across navigator routes), the
        // summary must NOT erase what the list never returned.
        //
        // This copies only fields the summary has nothing to say
        // about, so summary-over-summary is a no-op and no
        // "was the detail loaded?" discriminator is needed. It
        // matters because the list GET and the detail GET can be
        // in flight at the same time on a remount, which used to
        // wipe `presentation` (the dashboard theme) while keeping
        // the widgets, and reset the audit fields to "".
        const incoming = normalizeDashboard(incomingRaw);
        const existing = state.dashboards.find((d) => d.id === incoming.id);
        if (existing) {
          return {
            ...incoming,
            widgets: existing.widgets,
            positioning: existing.positioning,
            presentation: existing.presentation,
            created_by: existing.created_by || incoming.created_by,
            last_updated_by:
              existing.last_updated_by || incoming.last_updated_by,
          };
        }
        return incoming;
      });
    },

    addDashboard(state, action: PayloadAction<Dashboard>) {
      // Add new dashboard at the top of the list
      state.dashboards.unshift(normalizeDashboard(action.payload));
    },

    // Insert or replace a full dashboard (used after lazy-loading widgets via
    // GET /{id}, or syncing a PATCH response back into the list).
    upsertDashboard(state, action: PayloadAction<Dashboard>) {
      const incoming = normalizeDashboard(action.payload);
      const idx = state.dashboards.findIndex((d) => d.id === incoming.id);
      if (idx === -1) {
        state.dashboards.unshift(incoming);
      } else {
        state.dashboards[idx] = incoming;
      }
    },

    // Insert a dashboard right after the dashboard with the given id (used by
    // the server-side duplicate flow — doc §2.5.1: copy goes below the original).
    addDashboardAfter(
      state,
      action: PayloadAction<{ afterId: string; dashboard: Dashboard }>,
    ) {
      const incoming = normalizeDashboard(action.payload.dashboard);
      const idx = state.dashboards.findIndex(
        (d) => d.id === action.payload.afterId,
      );
      if (idx === -1) {
        state.dashboards.unshift(incoming);
      } else {
        state.dashboards.splice(idx + 1, 0, incoming);
      }
    },

    deleteDashboard(state, action: PayloadAction<string>) {
      const dashboardId = action.payload;

      // Remove from dashboards array
      state.dashboards = state.dashboards.filter((d) => d.id !== dashboardId);

      // Clear selection if deleting the selected dashboard
      if (state.selectedDashboardId === dashboardId) {
        state.selectedDashboardId = null;
      }

      // Clear default if deleting the default dashboard
      if (state.defaultDashboardId === dashboardId) {
        state.defaultDashboardId = null;
      }
    },

    setSelectedDashboard(state, action: PayloadAction<string | null>) {
      state.selectedDashboardId = action.payload;
    },

    setDefaultDashboard(state, action: PayloadAction<string>) {
      // Persistence happens in the action hook (patchDashboardV2UseCase).
      // Keep per-object is_default flags consistent with defaultDashboardId.
      state.defaultDashboardId = action.payload;
      state.dashboards.forEach((d) => {
        d.is_default = d.id === action.payload;
      });
    },

    updateDashboardMeta(
      state,
      action: PayloadAction<{
        id: string;
        name?: string;
        description?: string | null;
        status?: "draft" | "published";
        is_default?: boolean;
      }>,
    ) {
      // Used by meta-only PATCH endpoints (rename, publish, set-default)
      // so the slice doesn't blow out widgets + positioning. The earlier
      // handler round-tripped the BE response via `upsertDashboard`, which
      // replaced the entire dashboard object and triggered every data hook
      // downstream to re-run (multi-line fetch in particular — see
      // dashboards-ticket-list §patch-react-rerender). Only the fields
      // declare themselves as `undefined` are skipped; explicit `null` is
      // a valid clear (e.g. the user deleted the description).
      const dashboard = state.dashboards.find(
        (d) => d.id === action.payload.id,
      );
      if (!dashboard) return;
      const p = action.payload;
      if (p.name !== undefined) dashboard.name = p.name;
      if (p.description !== undefined) dashboard.description = p.description;
      if (p.status !== undefined) dashboard.status = p.status;
      if (p.is_default !== undefined) dashboard.is_default = p.is_default;
    },

    updateDashboardPresentation(
      state,
      action: PayloadAction<{
        dashboardId: string;
        /** `null` = wipe the whole presentation field. Otherwise partial
         *  merge into the existing presentation (any future groups the
         *  user doesn't touch are preserved). Sending `{ colors: null }`
         *  is the canonical "clear palette" shape; sending `null` clears
         *  the entire field (also legitimate, treated as no-theme). */
        presentation: DashboardPresentation | null;
      }>,
    ) {
      const { dashboardId, presentation } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (!dashboard) return;

      if (presentation === null) {
        // The caller wiped the entire field (e.g. BE PATCH body sent
        // `presentation: null`). Drop the key so `dashboard.presentation`
        // reads as `undefined` everywhere downstream — same shape as a
        // dashboard created before the picker shipped.
        dashboard.presentation = undefined;
      } else {
        // Partial merge: keep any future presentation groups untouched so
        // a colors edit doesn't blow away a future `typography` block.
        dashboard.presentation = {
          ...dashboard.presentation,
          ...presentation,
        };
      }
      dashboard.updated_at = new Date().toISOString();
    },

    updateWidgetPositioning(
      state,
      action: PayloadAction<{
        dashboardId: string;
        positioning: Record<string, WidgetPositioning>;
      }>,
    ) {
      const { dashboardId, positioning } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (dashboard) {
        dashboard.positioning = positioning;
        dashboard.updated_at = new Date().toISOString();
        // TODO: Call backend PATCH .../positioning endpoint to persist positions
      }
    },

    // Splice a server-committed widget into the dashboard. Used by the
    // server-side duplicate flow (`POST /widgets/{id}/duplicate`) — a fresh
    // widget id is fully server-assigned, no temp-pending entry exists to
    // retire first. Distinct from `commitPendingWidget`, which swaps a
    // local `pending-{uuid}` entry for the server's real id. Positioning
    // is the local pick forwarded to the BE via `widget_positioning` —
    // the grid watchers re-render once the splice lands; if the BE later
    // nudges placement (e.g. server-side "below the original" rule per
    // doc §2.5.1), the next PATCH positioning refresh wins.
    addServerCommittedWidget(
      state,
      action: PayloadAction<{
        dashboardId: string;
        widget: Widget;
        positioning: WidgetPositioning;
      }>,
    ) {
      const { dashboardId, widget, positioning } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (!dashboard) return;
      dashboard.widgets[widget.id] = widget;
      dashboard.positioning[widget.id] = positioning;
      dashboard.updated_at = new Date().toISOString();
    },

    // Picker-side: insert a widget into a dashboard with a temporary
    // `pending-{uuid}` id WITHOUT calling the backend. The drawer will POST /
    // PUT on Save (→ `commitPendingWidget`) or remove on Cancel (→
    // `removePendingWidget`).
    addPendingWidget(
      state,
      action: PayloadAction<{
        dashboardId: string;
        widget: Widget;
        positioning: WidgetPositioning;
      }>,
    ) {
      const { dashboardId, widget, positioning } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (!dashboard) return;
      dashboard.widgets[widget.id] = widget;
      dashboard.positioning[widget.id] = positioning;
      dashboard.updated_at = new Date().toISOString();
      if (!state.pendingWidgetIds.includes(widget.id)) {
        state.pendingWidgetIds.push(widget.id);
      }
    },

    // Drop the temp-pending widget and replace it with the server's real id
    // + payload. `positioning` carries over from the temp (the local slot the
    // picker chose), since `POST /widgets/from-template` only echoes the
    // widget body, not its position — the refresh that follows syncs the
    // server-truth positioning anyway.
    commitPendingWidget(
      state,
      action: PayloadAction<{
        dashboardId: string;
        tempId: string;
        realWidget: Widget;
      }>,
    ) {
      const { dashboardId, tempId, realWidget } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (!dashboard) return;
      const carriedPos = dashboard.positioning[tempId];
      delete dashboard.widgets[tempId];
      delete dashboard.positioning[tempId];
      dashboard.widgets[realWidget.id] = realWidget;
      if (carriedPos) dashboard.positioning[realWidget.id] = carriedPos;
      state.pendingWidgetIds = state.pendingWidgetIds.filter(
        (id) => id !== tempId,
      );
    },

    // Cancel path (drawer close, Esc, backdrop). The pending widget is purely
    // local, so wiping it has no backend side effects.
    removePendingWidget(
      state,
      action: PayloadAction<{ dashboardId: string; tempId: string }>,
    ) {
      const { dashboardId, tempId } = action.payload;
      const dashboard = state.dashboards.find((d) => d.id === dashboardId);
      if (!dashboard) return;
      delete dashboard.widgets[tempId];
      delete dashboard.positioning[tempId];
      state.pendingWidgetIds = state.pendingWidgetIds.filter(
        (id) => id !== tempId,
      );
    },
  },
});

export const {
  setDashboards,
  addDashboard,
  addDashboardAfter,
  upsertDashboard,
  updateDashboardMeta,
  updateDashboardPresentation,
  deleteDashboard,
  setSelectedDashboard,
  setDefaultDashboard,
  updateWidgetPositioning,
  addServerCommittedWidget,
  addPendingWidget,
  commitPendingWidget,
  removePendingWidget,
} = dashboardsSlice.actions;

export const DEFAULT_DASHBOARD_COLUMN_COUNT = DEFAULT_COLUMN_COUNT;

export default dashboardsSlice.reducer;
